import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { type PlanConfig, formatPrice } from '@/lib/stripe';

interface PlanCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  plan: PlanConfig | null;
  isYearly: boolean;
}

const signupSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type SignupFormData = z.infer<typeof signupSchema>;
type LoginFormData = z.infer<typeof loginSchema>;

type Step = 'auth' | 'checkout';

export function PlanCheckoutModal({ open, onClose, plan, isYearly }: PlanCheckoutModalProps) {
  const { t } = useTranslation();
  const { isAuthenticated, user, organization } = useAuth();
  
  const [authCompleted, setAuthCompleted] = useState(false);
  const [step, setStep] = useState<Step>('auth');
  const [isLoading, setIsLoading] = useState(false);
  const [authTab, setAuthTab] = useState<'signup' | 'login'>('signup');

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '' },
  });

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setAuthCompleted(false);
      if (isAuthenticated) {
        setStep('checkout');
      } else {
        setStep('auth');
      }
      signupForm.reset();
      loginForm.reset();
    } else {
      onClose();
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: { full_name: data.fullName },
        },
      });

      if (error) throw error;

      toast.success(t('landing.checkout.signupSuccess'));
      setAuthCompleted(true);
      setStep('checkout');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Signup failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) throw error;

      toast.success(t('landing.checkout.loginSuccess'));
      setAuthCompleted(true);
      setStep('checkout');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!plan) return;
    
    setIsLoading(true);
    try {
      const priceId = isYearly ? plan.yearlyPriceId : plan.monthlyPriceId;
      if (!priceId) throw new Error('Price not available');

      const orgId = organization?.id;
      const orgName = organization?.name;

      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: {
          priceId,
          organizationId: orgId,
          organizationName: orgName,
          mode: 'subscription',
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const checkoutUrl = data?.url;
      if (!checkoutUrl) throw new Error('No checkout URL received from server');

      sessionStorage.removeItem('purchase_flow');
      
      const isInIframe = window.self !== window.top;
      if (isInIframe) {
        try {
          window.top!.location.href = checkoutUrl;
        } catch (e) {
          window.open(checkoutUrl, '_blank');
          setIsLoading(false);
        }
      } else {
        window.location.href = checkoutUrl;
      }
      
      setTimeout(() => {
        if (isLoading) {
          window.open(checkoutUrl, '_blank');
          setIsLoading(false);
        }
      }, 2000);
      
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Checkout failed';
      toast.error(message);
      setIsLoading(false);
    }
  };

  const priceDisplay = plan 
    ? formatPrice(isYearly ? plan.yearlyPrice : plan.monthlyPrice)
    : '';
  const intervalDisplay = isYearly ? t('landing.pricing.perYear') : t('landing.pricing.perMonth');

  const isUserAuthenticated = isAuthenticated || authCompleted;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {(['auth', 'checkout'] as Step[]).map((s, idx) => {
        const isActive = s === step;
        const isPast = s === 'auth' && step === 'checkout';
        
        return (
          <div key={s} className="flex items-center gap-2">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                isActive 
                  ? 'bg-primary text-primary-foreground' 
                  : isPast 
                    ? 'bg-success text-success-foreground'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {isPast ? <Check className="h-4 w-4" /> : idx + 1}
            </div>
            {idx < 1 && (
              <div className={`w-8 h-0.5 ${isPast ? 'bg-success' : 'bg-muted'}`} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === 'auth' && t('landing.checkout.createAccount')}
            {step === 'checkout' && t('landing.checkout.completeSubscription')}
          </DialogTitle>
          <DialogDescription>
            {plan && (
              <span className="font-medium">
                {t(plan.nameKey)} - {priceDisplay}{intervalDisplay}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        {/* Step 1: Auth */}
        {step === 'auth' && !isUserAuthenticated && (
          <Tabs value={authTab} onValueChange={(v) => setAuthTab(v as 'signup' | 'login')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signup">{t('landing.checkout.signUp')}</TabsTrigger>
              <TabsTrigger value="login">{t('landing.checkout.logIn')}</TabsTrigger>
            </TabsList>

            <TabsContent value="signup" className="mt-4">
              <Form {...signupForm}>
                <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                  <FormField
                    control={signupForm.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('landing.checkout.fullName')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('landing.checkout.fullNamePlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={signupForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('landing.checkout.email')}</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder={t('landing.checkout.emailPlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={signupForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('landing.checkout.password')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder={t('landing.checkout.passwordPlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={signupForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('landing.checkout.confirmPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder={t('landing.checkout.confirmPasswordPlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('landing.checkout.createAccountBtn')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="login" className="mt-4">
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('landing.checkout.email')}</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder={t('landing.checkout.emailPlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('landing.checkout.password')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder={t('landing.checkout.passwordPlaceholder')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('landing.checkout.logInBtn')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </Form>
            </TabsContent>
          </Tabs>
        )}

        {/* Already authenticated */}
        {step === 'auth' && isUserAuthenticated && (
          <div className="text-center py-4">
            <p className="text-muted-foreground mb-4">
              {t('landing.checkout.loggedInAs', { email: user?.email })}
            </p>
            <Button onClick={() => setStep('checkout')}>
              {t('common.next')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 2: Checkout */}
        {step === 'checkout' && (
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4">
              <h4 className="font-medium mb-2">{t('landing.checkout.orderSummary')}</h4>
              <div className="flex justify-between text-sm">
                <span>{plan ? t(plan.nameKey) : ''}</span>
                <span className="font-medium">{priceDisplay}{intervalDisplay}</span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {t('landing.checkout.stripeRedirect')}
            </p>

            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setStep('auth')}
                disabled={isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.back')}
              </Button>
              <Button onClick={handleCheckout} className="flex-1" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('landing.checkout.proceedToPayment')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
